import React, { useState, useEffect, useRef } from "react";
import { Box, Input, IconButton, useColorModeValue } from "@chakra-ui/react";
import { FaCalendarAlt, FaTimes } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import { ptBR } from "date-fns/locale/pt-BR";
import "./CustomDatePicker.css";

registerLocale("pt-BR", ptBR);

interface CustomDatePickerProps {
  dateRange: [Date | null, Date | null];
  onChange: (dates: [Date | null, Date | null]) => void;
  selectsRange?: boolean;
  maxDays?: number; // Opcional - padrão 35 dias
  /** Tamanho da fonte do input. Default mantém o tamanho histórico. */
  fontSize?: string | Record<string, string>;
  /** Versão compacta (input + popup menores). Não afeta as demais telas. */
  compact?: boolean;
  /**
   * Só o calendário, sempre visível e centralizado, no fluxo — sem o input
   * nem os ícones de abrir/fechar/limpar. Ex.: dentro de um modal cujo foco é o
   * período; o intervalo escolhido aparece destacado no próprio calendário.
   */
  inline?: boolean;
  /**
   * Data única (sem intervalo): o valor fica em `dateRange[0]` e `dateRange[1]`
   * é sempre null. `maxDays` não se aplica. Ex.: vencimento de um boleto.
   */
  single?: boolean;
  /** Só no modo `single`: menor data selecionável (ex.: expiração ≥ vencimento). */
  minDate?: Date;
}

const CustomDatePicker = React.forwardRef<HTMLDivElement, CustomDatePickerProps>(
  function CustomDatePicker(
    {
      dateRange,
      onChange,
      selectsRange = false,
      maxDays = 35,
      fontSize = { base: "0.97rem", md: "0.97rem" },
      compact = false,
      inline = false,
      single = false,
      minDate,
    },
    _ref
  ) {

  const [open, setOpen] = useState(false);
  const [displayDate, setDisplayDate] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const bg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (single) {
      setDisplayDate(dateRange[0] ? dateRange[0].toLocaleDateString("pt-BR") : "");
    } else if (dateRange[0] && dateRange[1]) {
      const start = dateRange[0].toLocaleDateString("pt-BR");
      const end = dateRange[1].toLocaleDateString("pt-BR");
      setDisplayDate(`${start} - ${end}`);
    } else if (dateRange[0] && selectsRange) {
      setDisplayDate(dateRange[0].toLocaleDateString("pt-BR"));
    } else {
      setDisplayDate("");
    }
  }, [dateRange, selectsRange, single]);

  const handleSingleDateChange = (date: Date | null) => {
    if (!date) {
      onChange([null, null]);
      return;
    }
    const clean = new Date(date);
    clean.setHours(0, 0, 0, 0);
    onChange([clean, null]);
    setErrorMessage(null);
    setOpen(false);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onChange([null, null]);
    setDisplayDate("");
    setErrorMessage(null);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
      setOpen(false);
      setErrorMessage(null);
    }
  };

  useEffect(() => {
    if (open && !inline) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, inline]);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;

    // Se ainda não selecionou a data final → permitir
    if (start && !end) {
      const newStart = new Date(start);
      newStart.setHours(0, 0, 0, 0);
      onChange([newStart, null]);
      return;
    }

    // Quando selecionou as duas datas
    if (start && end) {
      const startClean = new Date(start);
      startClean.setHours(0, 0, 0, 0);

      const endClean = new Date(end);
      endClean.setHours(23, 59, 59, 999);

      const diffTime = endClean.getTime() - startClean.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 pois inclui ambos os dias

      if (diffDays > maxDays) {
        setErrorMessage(`O intervalo máximo permitido é de ${maxDays} ${maxDays === 1 ? ' dia' : ' dias'}`);
        // Mantém apenas a data inicial selecionada
        onChange([startClean, null]);
        return;
      }

      setErrorMessage(null);
      onChange([startClean, endClean]);

      // Fecha o calendário após selecionar intervalo válido
      if (selectsRange) {
        setOpen(false);
      }
    }
  };

  // O input compacto é estreito; o placeholder e, principalmente, o intervalo
  // selecionado ("dd/mm/aaaa - dd/mm/aaaa") cortam com a fonte normal — então
  // usamos uma fonte menor para tudo no modo compacto.
  const inputFontSize = compact ? "10px" : fontSize;
  // Sem data selecionada só há o ícone de calendário (o "limpar" some), então
  // no modo compacto reduzimos o padding direito para o placeholder não cortar.
  const inputPaddingRight = compact && !dateRange[0] ? 9 : 20;

  const showCalendar = inline || open;

  const renderHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
  }: {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
  }) => (
    <div className="custom-header">
      <button onClick={decreaseMonth}>←</button>
      <span>
        {date.toLocaleDateString("pt-BR", {
          year: "numeric",
          month: "long",
        })}
      </span>
      <button onClick={increaseMonth}>→</button>
    </div>
  );

  return (
    <Box position="relative" ref={calendarRef}>
      {!inline && (
        <>
          <Input
            placeholder={single ? "Selecione a data" : "Selecione o intervalo de datas"}
            value={displayDate}
            readOnly
            onClick={() => setOpen(!open)}
            bg={bg}
            borderColor={borderColor}
            size="md"
            paddingRight={inputPaddingRight} // espaço para os ícones
            height={{ base: "3.5rem", md: "3rem" }}
            width="100%"
            fontSize={inputFontSize}
            cursor="pointer"
            color="black"
            fontFamily="Lexend Deca"
          />

          <IconButton
            aria-label={single ? "Selecionar data" : "Selecionar intervalo de datas"}
            icon={<FaCalendarAlt />}
            position="absolute"
            right={dateRange[0] ? "2.8rem" : "0.5rem"}
            top="50%"
            transform="translateY(-50%)"
            onClick={() => setOpen(!open)}
            size="sm"
            variant="ghost"
            colorScheme="blue"
          />
        </>
      )}

      {!inline && dateRange[0] && (
        <IconButton
          aria-label={single ? "Limpar data" : "Limpar intervalo de datas"}
          icon={<FaTimes />}
          position="absolute"
          right="0.5rem"
          top="50%"
          transform="translateY(-50%)"
          onClick={handleClear}
          size="sm"
          variant="ghost"
          colorScheme="red"
        />
      )}

      {showCalendar && (
        <Box
          position={inline ? "static" : "absolute"}
          zIndex="10"
          mt={inline ? 0 : "2"}
          mx={inline ? "auto" : undefined}
          w={inline ? "fit-content" : undefined}
          boxShadow="lg"
          borderRadius="md"
          overflow="hidden"
          bg={bg}
          className={compact ? "dp-compact" : undefined}
        >
          {single ? (
            <DatePicker
              selected={dateRange[0]}
              onChange={handleSingleDateChange}
              inline
              locale="pt-BR"
              monthsShown={1}
              className="chakra-datepicker"
              minDate={minDate}
              renderCustomHeader={renderHeader}
            />
          ) : (
            <DatePicker
              selected={dateRange[0]}
              onChange={handleDateChange}
              startDate={dateRange[0] ?? undefined}
              endDate={dateRange[1] ?? undefined}
              selectsRange
              inline
              locale="pt-BR"
              monthsShown={1}
              className="chakra-datepicker"
              minDate={dateRange[0] ?? undefined} // impede selecionar data anterior à inicial
              renderCustomHeader={renderHeader}
            />
          )}

          {errorMessage && (
            <Box p={3} bg="red.100" color="red.700" fontSize="sm">
              {errorMessage}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
});

export default CustomDatePicker;